import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";

import { Flex } from "../../../styles/utility-components";
import { TargetType, TextLink } from "../../actions/Links";
import Tooltip, { TooltipComponentProps } from "./Tooltip";

type EllipsisTooltipProps = {
  fullText: string;
  tooltipProps?: Omit<TooltipComponentProps, "children" | "content">;
  children: (ref: React.RefCallback<HTMLElement>) => React.ReactElement;
};

/**
 * Renders a component and if the child the ref is attached to is detected to have been truncated or is overflowing show a tooltip on hover against the root element.
 *
 * The component the ref is attached to is responsible for ensuring truncation/overflowing styles are applied (e.g. via `<Text ellipsis />`).
 *
 * The tooltip will be a sibling to the top child element and use it for positioning & for hover detection.
 * For this reason it **CANNOT NOT be a fragment `<>`** as otherwise the tooltip will never show.
 *
 * @param fullText The full text to display in the tooltip when content is truncated/overflowing
 * @param tooltipProps Optional props to pass to the underlying Tooltip component
 * @param children Render prop that receives a ref callback to attach to the element to monitor for truncation/overflowing. Also used as the content to render.
 *
 * @example
 * <EllipsisTooltip fullText="This is the full text">
 *   {(ref) => (
 *     <div>
 *       Tooltip is centered on this div
 *       <Text ref={ref} ellipsis>
 *         This is the full text
 *       </Text>
 *     </div>
 *   )}
 * </EllipsisTooltip>
 */
function EllipsisTooltip({
  fullText,
  tooltipProps,
  children,
}: EllipsisTooltipProps): React.ReactNode {
  const [element, setElement] = useState<HTMLElement | null>(null); // Not using useRef since we do want to re-render when ref changes

  // We need 2 states since both operations can happen independently and we want to avoid a race condition
  // e.g. tooltip is requested due to tabbing (which is immediate since there is no hover delay)
  // but the ResizeObserver hasn't yet detected the overflow state and updated canShowTooltip
  // Therefore we have to treat both separately and only combine them where they are used
  const [canShowTooltip, setCanShowTooltip] = useState(false);
  const [tooltipRequested, setTooltipRequested] = useState(false);

  // Create a ResizeObserver that monitors the element for size changes
  const resizeObserver = useMemo(() => {
    return new ResizeObserver((entries) => {
      for (const entry of entries) {
        const elem = entry.target as HTMLElement;
        // Detect overflow since offsetWidth/Height will be less than scrollWidth/Height if it is overflowing
        setCanShowTooltip(
          elem.offsetWidth < elem.scrollWidth || elem.offsetHeight < elem.scrollHeight,
        );
      }
    });
  }, []);

  useEffect(() => {
    if (element) {
      // Set initial overflow state when element is first attached/mounted
      setCanShowTooltip(
        element.offsetWidth < element.scrollWidth || element.offsetHeight < element.scrollHeight,
      );

      // Observe element for changes
      resizeObserver.observe(element);
      // Cleanup on unmount or when element changes
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [element, resizeObserver]);

  // Render the child, passing setElement as the ref callback so we can measure the child if it is overflowing or not.
  const content = children(setElement);

  // Need to always render the tooltip since conditionally rendering it causes the DOM element to be recreated
  // meaning tab focus can be lost and stopping the user being able to access the rest of the page with the keyboard
  return (
    <Tooltip
      {...tooltipProps}
      content={fullText}
      open={tooltipRequested && canShowTooltip}
      onOpenChange={setTooltipRequested}
    >
      {content}
    </Tooltip>
  );
}

export default EllipsisTooltip;

type EllipsisTooltipTextProps = {
  fullText: string;
  tooltipProps?: Omit<TooltipComponentProps, "children" | "content">;
  to: string;
  targetType?: TargetType;
  maxWidth?: number;
};

export function EllipsisTooltipTextLink(props: EllipsisTooltipTextProps): React.ReactNode {
  return (
    <EllipsisTooltip {...props}>
      {(ref) => (
        <LinkContainer maxWidth={props.maxWidth}>
          <TextLink to={props.to} targetType={props.targetType} ref={ref} ellipsis>
            {props.fullText}
          </TextLink>
        </LinkContainer>
      )}
    </EllipsisTooltip>
  );
}

const LinkContainer = styled(Flex)<{ maxWidth?: number }>(({ maxWidth }) => ({
  maxWidth,
}));
